import * as _ from 'underscore';
import { Messages } from '../../../../codegen/Localize';
import { ExtractEncryptionState, IDataSourceInfo } from '../../../../ts/models/IDataSourceInfo';
import { IWorkbookInfo } from '../../../../ts/models/IWorkbookInfo';
import { IExplorableItemTypeInfo, ISpaceTypeInfo } from '../../../../ts/models/resourceInfoParts';
import { IExtractConvertAction } from '../../../../ts/ServerApi/types/actionNames';
import { createExtractsMethod } from '../../../actions/spaces/RunCreateExtractsAction';
import { getAppStore } from '../../../AppStore';
import { selectContentFromAppState } from '../../../selectors/ContentSelectors';
import { CompositeSessionInfoSelectors, SessionSiteSelectors } from '../../../selectors/SessionInfoSelectors';
import { selectSpaceInfo, selectSpaceInfoItem } from '../../../selectors/SpaceInfoSelectors';
import { AppState } from '../../../state/AppState';
import { isWorkbookSpace, SpaceInfo } from '../../../state/SpaceInfo';
import { EntityActionNames, IEntityAction } from './IEntityAction';
import {
  isAllExtractDatasource,
  isAnyDatasoursEncrypted,
  isAnyDatasoursUnencrypted,
  isAnyLiveNonFileBasedNorSqlProxyDatasource,
  isAnyPendingExtractStatus,
  isDatasoursAllLive,
  isEncryptionEnabled,
  isEncryptionEnforced,
  isWorkbookEncryptedExtract,
  userCanCreateExtractOnEmbeddedDs } from './IEntityActionHelpers';
import { ExtractEncryptionMode } from '../../../../sites/SitePropertyTypes';


export const CreateEncryptedExtractForEmbeddedDsAction: IEntityAction<IDataSourceInfo, IExtractConvertAction> = {
    name: EntityActionNames.CreateExtractsEmbEncrypted,
    translateName: Messages.createExtractsForDsSubActionEncrypted,
    isVisible: (targetItems: IDataSourceInfo[], state: AppState, availableActions: IExtractConvertAction) => {
      return isVisibleForFlyoutButton(state, targetItems);
    },
    isClickable: (availableActions: IExtractConvertAction, targetItems: IDataSourceInfo[], state: AppState) => {
      return true;
    },
    isChecked: (availableActions: IExtractConvertAction, targetItems: IDataSourceInfo[], state: AppState) => {
      return false;
    },
    createReduxAction: (targetItems: IDataSourceInfo[]) => createExtractsMethod(targetItems, ExtractEncryptionState.encrypted)
};

export const CreateUnencryptedExtractForEmbeddedDsAction: IEntityAction<IDataSourceInfo, IExtractConvertAction> = {
  name: EntityActionNames.CreateExtractsEmbUnencrypted,
  translateName: Messages.createExtractsForDsSubActionUnencrypted,
  isVisible: (targetItems: IDataSourceInfo[], state: AppState, availableActions: IExtractConvertAction) => {
    return isVisibleForFlyoutButton(state, targetItems);
  },
  isClickable: (availableActions: IExtractConvertAction, targetItems: IDataSourceInfo[], state: AppState) => {
    return true;
  },
  isChecked: (availableActions: IExtractConvertAction, targetItems: IDataSourceInfo[], state: AppState) => {
    return false;
  },
  createReduxAction: (targetItems: IDataSourceInfo[]) => createExtractsMethod(targetItems, ExtractEncryptionState.unencrypted)
};

export const CreateExtractForEmbeddedDsAction: IEntityAction<IDataSourceInfo, IExtractConvertAction> = {
  name: EntityActionNames.CreateExtractsEmb,
  translateName: (targetItems: IDataSourceInfo[], state: AppState) => {
    if (showEncryptedButtonName(state, targetItems)) {
      return Messages.createExtractsForDsActionEncrypted();
    } else if (showUnencryptedButtonName(state, targetItems)) {
      return Messages.createExtractsForDsActionUnencrypted();
    } else {
      return Messages.createExtractForDsAction();
    }
  },
  isVisible: (targetItems: IDataSourceInfo[], state: AppState, availableActions: IExtractConvertAction) => {
    const spaceInfo: SpaceInfo = selectSpaceInfo(state);
    return CompositeSessionInfoSelectors.isWebExtractionEnabled(state) && isWorkbookSpace(spaceInfo);
  },
  isClickable: (availableActions: IExtractConvertAction, targetItems: IDataSourceInfo[], state: AppState) => {
    return userCanCreateExtractOnEmbeddedDs(state) && isClickableForExtractButton(state, targetItems);
  },
  isChecked: (availableActions: IExtractConvertAction, targetItems: IDataSourceInfo[], state: AppState) => {
    return isCheckedForExtractButton(state, targetItems);
  },
  createReduxAction: (targetItems: IDataSourceInfo[]) => createExtractsMethod(targetItems, SetExtractEncryptionType(getAppStore().getState()))
};

function SetExtractEncryptionType(state: AppState) {
  const workbook: ISpaceTypeInfo  = selectSpaceInfoItem(state);
  return (SessionSiteSelectors.extractEncryptionMode(getAppStore().getState()) === ExtractEncryptionMode.ENFORCED || isWorkbookEncryptedExtract(workbook as IWorkbookInfo))  ?
  ExtractEncryptionState.encrypted : ExtractEncryptionState.unencrypted;
}

function isVisibleForFlyoutButton(state: AppState, targetItems: IDataSourceInfo[]) {
  const spaceInfo: SpaceInfo = selectSpaceInfo(state);
  const datasources: ReadonlyArray<IExplorableItemTypeInfo> = selectContentFromAppState(state);
  // if workbook has embedded datasources are live, will show flyout button
  return CompositeSessionInfoSelectors.isWebExtractionEnabled(state) && isWorkbookSpace(spaceInfo) &&
  isEncryptionEnabled(state) && isDatasoursAllLive(_.map(datasources, ds =>ds as IDataSourceInfo));
}


function  isClickableForExtractButton(state: AppState, targetItems: IDataSourceInfo[]) {
  const datasources: ReadonlyArray<IExplorableItemTypeInfo> = selectContentFromAppState(state);
  // case: 1) No pending status
  //       2) select any live and non-file nor non-sqlproxy based datasource
  return !isAnyPendingExtractStatus(_.map(datasources, ds =>ds as IDataSourceInfo)) &&
  isAnyLiveNonFileBasedNorSqlProxyDatasource(targetItems);
}

function isCheckedForExtractButton(state: AppState, targetItems: IDataSourceInfo[]) {
  // case: 1) select all/single extract based embedded datasource
  return isAllExtractDatasource(targetItems);
}

function showUnencryptedButtonName(state: AppState, targetItems: IDataSourceInfo[]) {
  // case Encryption enable workbook has Unencrypted extract datasource
  const datasources: ReadonlyArray<IExplorableItemTypeInfo> = selectContentFromAppState(state);
  return  (isEncryptionEnabled(state) && isAnyDatasoursUnencrypted(_.map(datasources, ds =>ds as IDataSourceInfo)));
}

function showEncryptedButtonName(state: AppState, targetItems: IDataSourceInfo[]) {
  // case 1) Encryption Forced
  //      2) Encryption enable workbook has Encrypted extract datasource
  const datasources: ReadonlyArray<IExplorableItemTypeInfo> = selectContentFromAppState(state);
  return isEncryptionEnforced(state) ||
  (isEncryptionEnabled(state) && isAnyDatasoursEncrypted(_.map(datasources, ds =>ds as IDataSourceInfo)));
}
